import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { SegmentedControl, Text } from "@mantine/core";
import {
  IconClipboardList,
  IconLink,
  IconPlus,
  IconWallet,
} from "@tabler/icons-react";
import ReactApexChart from "react-apexcharts";
import OperatorChartCard from "./OperatorChartCard";
import OperatorSummaryCards from "./OperatorSummaryCards";
import op from "./OperatorDashboardView.module.css";
import {
  buildAgeGenderChartOptions,
  buildPaymentTrendOptions,
  buildPolicyStatusBreakdownOptions,
  buildTrendChartOptions,
  buildWalletUsageOptions,
  CHART_MIN_HEIGHT,
} from "../../utils/operatorDashboardCharts";

const CARD_TIME_OPTIONS = [
  { label: "Daily", value: "D" },
  { label: "Weekly", value: "W" },
  { label: "Monthly", value: "M" },
];

const QUICK_ACTIONS = [
  {
    icon: IconPlus,
    title: "Issue Policy",
    description: "Single traveller issuance",
    path: "/issuance/single-issuance",
  },
  {
    icon: IconLink,
    title: "Create Quote",
    description: "Send a shareable quote link",
    path: "/issuance/quote-links",
  },
  {
    icon: IconWallet,
    title: "Wallet",
    description: "View balance & top up",
    path: "/operator/operator-details/wallet",
  },
  {
    icon: IconClipboardList,
    title: "Your Plans",
    description: "See plans available to you",
    path: "/operator/operator-details/operator-assign-covers",
  },
];

function OperatorDashboardContent({
  summaryCards,
  isLoading,
  timeFrame,
  walletUsageView,
  setWalletUsageView,
  paymentTrendView,
  setPaymentTrendView,
  trendCategories,
  trendValues,
  hasTrendData,
  statusLabels,
  statusValues,
  hasPolicyData,
  ageCategories,
  maleSeries,
  femaleSeries,
  hasTravellerData,
  walletCategories,
  rechargeSeries,
  spendSeries,
  hasWalletData,
  paymentCategories,
  paymentValues,
  hasPaymentData,
}) {
  const navigate = useNavigate();

  const trendOptions = useMemo(
    () => buildTrendChartOptions(trendCategories, timeFrame),
    [trendCategories, timeFrame]
  );
  const policyOptions = useMemo(
    () => buildPolicyStatusBreakdownOptions(statusLabels),
    [statusLabels]
  );
  const ageGenderOptions = useMemo(
    () => buildAgeGenderChartOptions(ageCategories),
    [ageCategories]
  );
  const walletOptions = useMemo(
    () => buildWalletUsageOptions(walletCategories, walletUsageView),
    [walletCategories, walletUsageView]
  );
  const paymentOptions = useMemo(
    () => buildPaymentTrendOptions(paymentCategories, paymentTrendView),
    [paymentCategories, paymentTrendView]
  );

  const chartTimeControl = (value, onChange) => (
    <SegmentedControl
      size="xs"
      className={op.cardSegment}
      value={value}
      onChange={onChange}
      aria-label="Chart time period"
      data={CARD_TIME_OPTIONS}
    />
  );

  return (
    <div className={op.content}>
      <div className={op.stack}>
        <OperatorSummaryCards cards={summaryCards} loading={isLoading} />

        <OperatorChartCard
          title="Policy Trend"
          subtitle="Issued covers over the selected period"
          loading={isLoading}
          hasData={hasTrendData}
        >
          <ReactApexChart
            options={trendOptions}
            series={[{ name: "Policies", data: trendValues }]}
            type="area"
            height={CHART_MIN_HEIGHT}
          />
        </OperatorChartCard>

        <section className={op.section}>
          <div className={op.sectionHeader}>
            <Text className={op.sectionTitle} component="h2">
              Quick Actions
            </Text>
            <Text className={op.sectionSubtitle} component="p">
              Jump straight into your most common tasks
            </Text>
          </div>
          <div className={op.quickActions}>
            {QUICK_ACTIONS.map((action) => {
              const Icon = action.icon;
              return (
                <button
                  key={action.title}
                  type="button"
                  className={op.actionCard}
                  onClick={() => navigate(action.path)}
                >
                  <div className={op.actionIcon}>
                    <Icon size={20} />
                  </div>
                  <div className={op.actionContent}>
                    <Text className={op.actionTitle} component="p" m={0}>
                      {action.title}
                    </Text>
                    <Text className={op.actionDescription} component="p" m={0}>
                      {action.description}
                    </Text>
                  </div>
                </button>
              );
            })}
          </div>
        </section>

        <div className={op.bottomRow}>
          <OperatorChartCard
            title="Policy Status Breakdown"
            subtitle="Active, inactive and expired policies"
            loading={isLoading}
            hasData={hasPolicyData}
          >
            <ReactApexChart
              options={policyOptions}
              series={[{ name: "Policies", data: statusValues }]}
              type="bar"
              height={CHART_MIN_HEIGHT}
            />
          </OperatorChartCard>

          <OperatorChartCard
            title="Traveller Demographics"
            subtitle="Travellers covered by age group and gender"
            loading={isLoading}
            hasData={hasTravellerData}
          >
            <ReactApexChart
              options={ageGenderOptions}
              series={[
                { name: "Male", data: maleSeries },
                { name: "Female", data: femaleSeries },
              ]}
              type="bar"
              height={CHART_MIN_HEIGHT}
            />
          </OperatorChartCard>
        </div>

        <div className={op.bottomRow}>
          <OperatorChartCard
            title="Wallet Usage"
            subtitle="Credit vs debit over the period"
            trailing={chartTimeControl(walletUsageView, setWalletUsageView)}
            loading={isLoading}
            hasData={hasWalletData}
          >
            <ReactApexChart
              options={walletOptions}
              series={[
                { name: "Credit", data: rechargeSeries },
                { name: "Debit", data: spendSeries },
              ]}
              type="area"
              height={CHART_MIN_HEIGHT}
            />
          </OperatorChartCard>

          <OperatorChartCard
            title="Payment Trend"
            subtitle="Policy purchase value over time"
            trailing={chartTimeControl(paymentTrendView, setPaymentTrendView)}
            loading={isLoading}
            hasData={hasPaymentData}
          >
            <ReactApexChart
              options={paymentOptions}
              series={[{ name: "Payments", data: paymentValues }]}
              type="bar"
              height={CHART_MIN_HEIGHT}
            />
          </OperatorChartCard>
        </div>
      </div>
    </div>
  );
}

export default OperatorDashboardContent;
